/**
 * CaseContextSection
 *
 * Displays case context information with ability to add items to call prep.
 * Shows: Incident Summary, Incident details, Client details, Liability, Coverage, Treatment.
 * Read-only reference panel - settings like Case Manager and Contact Client are in the toolbar.
 */
import {
  Building2,
  FileText,
  Scale,
  Stethoscope,
  User,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { CoverageLink, MedicalProviderLink } from '@/components/entity-links';
import { cn } from '@/lib/utils';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { usePoliceInvestigationListQuery } from '@/services/api/policeInvestigationService';
import { usePoliceReportsListQuery } from '@/services/api/policeReportsService';
import type { DetailedMatter } from '@/services/types/client-intake-types';
import { CoverageCategory } from '@/services/types/coverage-types';
import { formatShortDate } from '@/utils/formatting';

import { CallPrepToggle } from './CallPrepToggle';
import { getPoliceReportTier } from './police-report-utils';
import { PoliceReportRow } from './PoliceReportRow';

interface CaseContextSectionProps {
  matter: DetailedMatter | undefined;
  matterId: string;
  toggleInCallPrep: (sourceId: string, text: string) => void;
  isInCallPrep: (sourceId: string) => boolean;
}

interface ContextRowProps {
  label: string;
  value: string | null | undefined;
  sourceId: string;
  callPrepText: string;
  toggleInCallPrep: (sourceId: string, text: string) => void;
  isInCallPrep: boolean;
}

export const ContextRow = ({
  label,
  value,
  sourceId,
  callPrepText,
  toggleInCallPrep,
  isInCallPrep,
}: ContextRowProps) => {
  const displayValue = value || '—';

  return (
    <div
      className={cn(
        'group flex items-center justify-between rounded px-2 py-1.5 transition-colors',
        'hover:bg-white dark:hover:bg-zinc-800',
        isInCallPrep && 'bg-amber-50 dark:bg-amber-900/20',
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 flex-shrink-0">
          {label}:
        </span>
        <span
          className={cn(
            'truncate text-xs',
            value
              ? 'text-zinc-900 dark:text-white font-medium'
              : 'text-zinc-400 dark:text-zinc-600',
          )}
          title={displayValue}
        >
          {displayValue}
        </span>
      </div>

      <CallPrepToggle
        sourceId={sourceId}
        callPrepText={callPrepText}
        isActive={isInCallPrep}
        onToggle={toggleInCallPrep}
      />
    </div>
  );
};

interface SectionCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  accent?: string;
}

const SectionCard = ({ title, icon, children, accent }: SectionCardProps) => (
  <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 overflow-hidden">
    <div className="flex items-center gap-1.5 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 px-3 py-2">
      <span className={cn('text-zinc-500 dark:text-zinc-400', accent)}>
        {icon}
      </span>
      <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
        {title}
      </h3>
    </div>
    <div className="p-1.5 space-y-0.5">{children}</div>
  </div>
);

/**
 * Get human-readable coverage category label
 */
const getCoverageCategoryLabel = (
  category: CoverageCategory | null | undefined,
): string => {
  switch (category) {
    case CoverageCategory.FIRST_PARTY:
      return '1P Auto';
    case CoverageCategory.THIRD_PARTY:
      return '3P';
    case CoverageCategory.FIRST_PARTY_HEALTH:
      return 'Health';
    default:
      return 'Insurance';
  }
};

/**
 * Get policy number from coverage policy_identifiers
 */
const getPolicyNumber = (
  policyIdentifiers:
    | { identifier_type: string; identifier_value: string }[]
    | null,
): string | null => {
  if (!policyIdentifiers || policyIdentifiers.length === 0) return null;
  const policyId = policyIdentifiers.find(
    (pi) => pi.identifier_type === 'POLICY_NUMBER',
  );
  return (
    policyId?.identifier_value || policyIdentifiers[0]?.identifier_value || null
  );
};

export const CaseContextSection = ({
  matter,
  matterId,
  toggleInCallPrep,
  isInCallPrep,
}: CaseContextSectionProps) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  // Fetch coverages from dedicated API
  const { data: coverages = [], isLoading: isCoveragesLoading } =
    useGetCoveragesQuery({ matterId }, { skip: !matterId });

  // Extract client info
  const primaryClient = useMemo(() => {
    if (!matter?.clients?.length) return null;
    return matter.clients[0];
  }, [matter?.clients]);

  // Get primary phone number from client
  const clientPhone = useMemo(() => {
    if (!primaryClient?.phone_numbers?.length) return null;
    return primaryClient.phone_numbers[0]?.phone_number;
  }, [primaryClient?.phone_numbers]);

  // Format client address
  const clientAddress = useMemo(() => {
    if (!primaryClient?.address) return null;
    const { line1, city, state, postcode } = primaryClient.address;
    const parts = [line1, city, state, postcode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }, [primaryClient?.address]);

  // Format DOB once (used for both display and call-prep text)
  const formattedDob = formatShortDate(primaryClient?.date_of_birth);

  // SSN: show full if available, otherwise show last 4 with its own label
  const ssnLabel = primaryClient?.ssn ? 'SSN' : 'Last 4 SSN';
  const ssnValue = primaryClient?.ssn || primaryClient?.last_four_of_ssn || null;

  // Extract medical providers
  const providers = useMemo(() => {
    return matter?.medical_notes || [];
  }, [matter?.medical_notes]);

  // Fetch police reports from dedicated API
  const { data: policeReports = [] } = usePoliceReportsListQuery(
    { matterId },
    { skip: !matterId },
  );

  // Fetch police investigation status
  const { data: investigationData } = usePoliceInvestigationListQuery(
    { matterId },
    { skip: !matterId },
  );

  // Backend returns single object (1-to-1 with matter) but codegen wraps as array
  const investigation = Array.isArray(investigationData)
    ? investigationData?.[0]
    : investigationData;

  // Determine police report tier (replaces manual attestation priority logic)
  const policeReportTier = useMemo(
    () => getPoliceReportTier(policeReports, investigation),
    [policeReports, investigation],
  );

  if (!matter) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
          No case information available
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Incident Summary */}
      <SectionCard
        title="Incident Summary"
        icon={<FileText className="h-3.5 w-3.5" />}
        accent="text-blue-600 dark:text-blue-400"
      >
        {matter.incident_note?.description ? (
          <div
            className={cn(
              'group flex items-start justify-between rounded px-2 py-1.5 transition-colors',
              'hover:bg-white dark:hover:bg-zinc-800',
              isInCallPrep('incident_summary') &&
                'bg-amber-50 dark:bg-amber-900/20',
            )}
          >
            <p className="text-xs leading-snug text-zinc-700 dark:text-zinc-300 font-medium whitespace-pre-wrap">
              {matter.incident_note.description.length > 200 && !isSummaryExpanded
                ? `${matter.incident_note.description.slice(0, 200)}... `
                : matter.incident_note.description}
              {matter.incident_note.description.length > 200 && (
                <>
                  {isSummaryExpanded && ' '}
                  <button
                    type="button"
                    onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                    className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                  >
                    {isSummaryExpanded ? 'show less' : 'show more'}
                  </button>
                </>
              )}
            </p>
            <CallPrepToggle
              sourceId="incident_summary"
              callPrepText={`Incident summary: ${matter.incident_note!.description}`}
              isActive={isInCallPrep('incident_summary')}
              onToggle={toggleInCallPrep}
              className="mt-0.5"
            />
          </div>
        ) : (
          <div
            className={cn(
              'group flex items-center justify-between rounded px-2 py-1.5 transition-colors',
              'hover:bg-white dark:hover:bg-zinc-800',
              isInCallPrep('no_incident_summary') &&
                'bg-amber-50 dark:bg-amber-900/20',
            )}
          >
            <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
              No incident summary on file
            </span>
            <CallPrepToggle
              sourceId="no_incident_summary"
              callPrepText="Follow up on missing incident summary"
              isActive={isInCallPrep('no_incident_summary')}
              onToggle={toggleInCallPrep}
            />
          </div>
        )}
      </SectionCard>

      {/* Incident */}
      <SectionCard
        title="Incident"
        icon={<FileText className="h-3.5 w-3.5" />}
        accent="text-red-600 dark:text-red-400"
      >
        <div>
          {/* Date of Loss */}
          <ContextRow
            label="Date"
            value={formatShortDate(matter.date_of_loss)}
            sourceId="date_of_loss"
            callPrepText={`Confirm incident date: ${formatShortDate(matter.date_of_loss)}`}
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('date_of_loss')}
          />
          {/* Location */}
          <ContextRow
            label="Location"
            value={
              matter.city && matter.state
                ? `${matter.city}, ${matter.state}`
                : matter.city || matter.state
            }
            sourceId="location"
            callPrepText={`Discuss incident location: ${matter.city}, ${matter.state}`}
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('location')}
          />
          {/* Case Type */}
          <ContextRow
            label="Type"
            value={matter.matter_type || matter.details_type}
            sourceId="case_type"
            callPrepText={`Case type: ${matter.matter_type || matter.details_type}`}
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('case_type')}
          />
        </div>
      </SectionCard>

      {/* Client */}
      <SectionCard
        title="Client"
        icon={<User className="h-3.5 w-3.5" />}
        accent="text-indigo-600 dark:text-indigo-400"
      >
        <div>
          {/* Client Name */}
          <ContextRow
            label="Name"
            value={primaryClient?.name}
            sourceId="client_name"
            callPrepText={`Client: ${primaryClient?.name || 'unknown'}`}
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('client_name')}
          />
          {/* Client DOB */}
          <ContextRow
            label="DOB"
            value={formattedDob}
            sourceId="client_dob"
            callPrepText={`Client DOB: ${formattedDob || 'missing'}`}
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('client_dob')}
          />
          {/* Client Phone */}
          <ContextRow
            label="Phone"
            value={clientPhone}
            sourceId="client_phone"
            callPrepText="Verify client contact information"
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('client_phone')}
          />
          {/* Client Email */}
          <ContextRow
            label="Email"
            value={primaryClient?.email}
            sourceId="client_email"
            callPrepText="Verify client email address"
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('client_email')}
          />
          {/* Client Address */}
          <ContextRow
            label="Address"
            value={clientAddress}
            sourceId="client_address"
            callPrepText="Verify client address"
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('client_address')}
          />
          {/* Client SSN */}
          <ContextRow
            label={ssnLabel}
            value={ssnValue}
            sourceId="client_ssn"
            callPrepText="Verify client SSN"
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep('client_ssn')}
          />
        </div>
      </SectionCard>

      {/* Liability */}
      <SectionCard
        title="Liability"
        icon={<Scale className="h-3.5 w-3.5" />}
        accent="text-orange-600 dark:text-orange-400"
      >
        <PoliceReportRow
          policeReportTier={policeReportTier}
          matterId={matterId}
          toggleInCallPrep={toggleInCallPrep}
          isInCallPrep={isInCallPrep}
        />
      </SectionCard>

      {/* Coverage */}
      <SectionCard
        title="Coverage"
        icon={<Building2 className="h-3.5 w-3.5" />}
        accent="text-green-600 dark:text-green-400"
      >
        <div>
          {isCoveragesLoading && (
            <div className="px-3 py-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">
              Loading...
            </div>
          )}
          {!isCoveragesLoading &&
            coverages.length > 0 &&
            coverages.map((coverage) => {
              const carrierName = coverage.carrier?.name || 'Unknown';
              const policyNumber = getPolicyNumber(coverage.policy_identifiers);
              const categoryLabel = getCoverageCategoryLabel(coverage.category);

              return (
                <div
                  key={coverage.id}
                  className={cn(
                    'group flex items-center justify-between rounded px-2 py-1.5 transition-colors',
                    'hover:bg-white dark:hover:bg-zinc-800',
                    isInCallPrep(`coverage_${coverage.id}`) &&
                      'bg-amber-50 dark:bg-amber-900/20',
                  )}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 flex-shrink-0">
                      {categoryLabel}:
                    </span>
                    <CoverageLink
                      entityId={coverage.id}
                      matterId={matterId}
                      className="truncate text-xs font-medium"
                    >
                      {carrierName}
                    </CoverageLink>
                    {policyNumber && (
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                        #{policyNumber}
                      </span>
                    )}
                  </div>
                  <CallPrepToggle
                    sourceId={`coverage_${coverage.id}`}
                    callPrepText={`Discuss ${categoryLabel} coverage: ${carrierName}${policyNumber ? ` (Policy #${policyNumber})` : ''}`}
                    isActive={isInCallPrep(`coverage_${coverage.id}`)}
                    onToggle={toggleInCallPrep}
                  />
                </div>
              );
            })}
          {!isCoveragesLoading && coverages.length === 0 && (
            <div
              className={cn(
                'group flex items-center justify-between rounded px-2 py-1.5 transition-colors',
                'hover:bg-white dark:hover:bg-zinc-800',
                isInCallPrep('no_coverage') &&
                  'bg-amber-50 dark:bg-amber-900/20',
              )}
            >
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                No coverage on file
              </span>
              <CallPrepToggle
                sourceId="no_coverage"
                callPrepText="Follow up on missing coverage information"
                isActive={isInCallPrep('no_coverage')}
                onToggle={toggleInCallPrep}
              />
            </div>
          )}
        </div>
      </SectionCard>

      {/* Treatment */}
      <SectionCard
        title="Treatment"
        icon={<Stethoscope className="h-3.5 w-3.5" />}
        accent="text-purple-600 dark:text-purple-400"
      >
        <div>
          {providers.length > 0 ? (
            providers.map((provider, index) => (
              <div
                key={provider.id || index}
                className={cn(
                  'group flex items-center justify-between rounded px-2 py-1.5 transition-colors',
                  'hover:bg-white dark:hover:bg-zinc-800',
                  isInCallPrep(`provider_${provider.id}`) &&
                    'bg-amber-50 dark:bg-amber-900/20',
                )}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 flex-shrink-0">
                    Provider:
                  </span>
                  <MedicalProviderLink
                    entityId={provider.id}
                    matterId={matterId}
                    className="truncate text-xs font-medium"
                  >
                    {provider.name || 'Unknown'}
                  </MedicalProviderLink>
                </div>
                <CallPrepToggle
                  sourceId={`provider_${provider.id}`}
                  callPrepText={`Discuss treatment at: ${provider.name || 'Unknown'}`}
                  isActive={isInCallPrep(`provider_${provider.id}`)}
                  onToggle={toggleInCallPrep}
                />
              </div>
            ))
          ) : (
            <div
              className={cn(
                'group flex items-center justify-between rounded px-2 py-1.5 transition-colors',
                'hover:bg-white dark:hover:bg-zinc-800',
                isInCallPrep('no_treatment') &&
                  'bg-amber-50 dark:bg-amber-900/20',
              )}
            >
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                No treatment on file
              </span>
              <CallPrepToggle
                sourceId="no_treatment"
                callPrepText="Follow up on missing treatment information"
                isActive={isInCallPrep('no_treatment')}
                onToggle={toggleInCallPrep}
              />
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
};
